import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/compiled';
import Tile, { type TileProps } from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

import generativeIndicatorSvg from '../../../assets/generative-indicator.svg';
import { MonochromeGenerativeIndicator } from '../generative-indicator/monochrome-generative-indicator';

import { messages } from './messages';

const styles = cssMap({
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

export interface PulseTileProps {
	/** Test ID for the tile container */
	testId?: string;
	/** Size of the tile */
	size?: TileProps['size'];
	/** Visual treatment for the generative indicator */
	appearance?: 'colorful' | 'monochrome';
	/** Background color for the tile surface */
	backgroundColor?: TileProps['backgroundColor'];
	/** Whether the tile should render a border */
	hasBorder?: TileProps['hasBorder'];
}

/**
 * PulseTile — A tile with a colorful generative indicator icon.
 */
export const PulseTile = ({
	testId = 'pulse-tile',
	size = 'xlarge',
	appearance = 'colorful',
	backgroundColor = 'color.background.neutral',
	hasBorder = false,
}: PulseTileProps): React.JSX.Element => {
	const intl = useIntl();

	return (
		<Tile
			label={intl.formatMessage(messages.pulseTileLabel)}
			size={size}
			backgroundColor={backgroundColor}
			hasBorder={hasBorder}
			testId={testId}
		>
			{appearance === 'monochrome' ? (
				<MonochromeGenerativeIndicator
					primaryColor={token('color.icon.subtle')}
					testId={`${testId}-single-color-monochrome-icon`}
				/>
			) : (
				<Box xcss={styles.iconWrapper}>
					<Image src={generativeIndicatorSvg} alt="" />
				</Box>
			)}
		</Tile>
	);
};
